import { useState } from "react";
import type { ReactNode } from "react";
import { useChunkContext } from "../../hooks/useChunkContext";
import type { Authority, AuthorityCondition } from "../../hooks/useCaseAuthorities";

/**
 * Authority infographic — the click-through behind a Shield Wall segment.
 * Two visual grammars keyed to how the instrument is governed:
 *   - conditional / until_event  → the GATEHOUSE (conditions are gates that lock/unlock powers)
 *   - date_range / perpetual     → the COUNTDOWN (a validity bar to expiry)
 * One instrument = its group of per-grant authority rows (shared source_label).
 */
export default function AuthorityInfographic({ group, onClose }: { group: Authority[]; onClose: () => void }) {
  if (group.length === 0) return null;
  const conditional = group.some((a) => a.validity_basis === "conditional" || a.validity_basis === "until_event");
  return conditional
    ? <Gatehouse group={group} onClose={onClose} />
    : <Countdown group={group} onClose={onClose} />;
}

function instrumentName(label: string | null, fallback: string): string {
  if (!label) return fallback;
  return label.replace(/\.pdf$/i, "").replace(/^[A-Z]-?\d+\s+/i, "").trim() || fallback;
}
function dedupe<T>(arr: T[], key: (t: T) => string): T[] {
  const seen = new Set<string>(); const out: T[] = [];
  for (const x of arr) { const k = key(x); if (!seen.has(k)) { seen.add(k); out.push(x); } }
  return out;
}
function fmtDate(d: string | null): string {
  if (!d) return "—";
  const t = new Date(d);
  return isNaN(t.getTime()) ? "—" : t.toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
}

function Shell({ kind, name, grantor, grantee, statusLabel, statusColor, onClose, children }: {
  kind: string; name: string; grantor: string | null; grantee: string | null;
  statusLabel: string; statusColor: string; onClose: () => void; children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded border border-steel-dark bg-iron/80 backdrop-blur-sm">
      <div className="flex items-start justify-between gap-4 border-b border-steel-dark/70 bg-black/30 px-6 py-4">
        <div className="min-w-0">
          <p className="font-display text-base font-light uppercase tracking-[0.3em] text-bone-mute">{kind}</p>
          <h3 className="mt-1 truncate font-display text-xl font-light text-bone" style={{ textShadow: "0 2px 14px rgba(0,0,0,0.6)" }}>{name}</h3>
          {(grantor || grantee) && (
            <p className="mt-0.5 font-display text-base font-light uppercase tracking-[0.08em] text-bone-mute/80">{grantor} → {grantee}</p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className="rounded px-2 py-1 font-display text-sm font-light uppercase tracking-[0.1em]" style={{ background: statusColor, color: "#0c0f12" }}>{statusLabel}</span>
          <button onClick={onClose} className="font-display text-base font-light uppercase tracking-[0.2em] text-bone-mute hover:text-ember-bright">✕</button>
        </div>
      </div>
      <div className="px-6 py-5">{children}</div>
    </div>
  );
}

/* ===================== GATEHOUSE (conditional) ===================== */

function Gatehouse({ group, onClose }: { group: Authority[]; onClose: () => void }) {
  const first = group[0]!;
  const gates = dedupe(group.flatMap((a) => a.conditions ?? []), (c) => (c.text || "").toLowerCase()).filter((c) => c.text);
  const breaches = dedupe(group.flatMap((a) => a.termination_triggers ?? []), (t) => (t.text || "").toLowerCase()).filter((t) => t.text);
  const open = group.some((a) => a.status === "in_force");

  return (
    <Shell kind="conditional instrument · the gatehouse" name={instrumentName(first.source_label, first.title)}
      grantor={first.grantor} grantee={first.grantee}
      statusLabel={open ? "wall standing" : "wall down"} statusColor={open ? "#e8703a" : "#6b7480"} onClose={onClose}>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[280px_1fr]">
        {/* GATES */}
        <div>
          <p className="mb-2 font-display text-base font-light uppercase tracking-[0.14em] text-bone-dim">condition gates</p>
          <ul className="space-y-2">
            {gates.length === 0 && <li className="font-display text-base text-bone-mute">// no gating conditions</li>}
            {gates.map((g, i) => <GateRow key={i} c={g} />)}
          </ul>
        </div>

        {/* POWERS */}
        <div>
          <p className="mb-2 font-display text-base font-light uppercase tracking-[0.14em] text-bone-dim">authorized powers · {group.length}</p>
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {group.map((a) => <PowerTile key={a.id} a={a} lit={a.status === "in_force"} />)}
          </div>
        </div>
      </div>

      {breaches.length > 0 && (
        <div className="mt-5">
          <p className="mb-2 font-display text-base font-light uppercase tracking-[0.14em] text-bone-dim">the wall breaches if</p>
          <div className="flex flex-wrap gap-2">
            {breaches.map((b, i) => (
              <span key={i} className="rounded border border-[#8a3b3b]/60 bg-[#8a3b3b]/10 px-2.5 py-1 font-display text-[12px] text-[#d39a9a]">⚔ {b.text}</span>
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
}

function Portcullis({ raised, color }: { raised: boolean; color: string }) {
  return (
    <svg width="30" height="34" viewBox="0 0 30 34" className="shrink-0" style={raised ? { filter: `drop-shadow(0 0 5px ${color})` } : undefined}>
      <rect x="3" y="2" width="24" height="4" fill={color} />
      {/* bars: raised = short (pulled up), lowered = full with spikes */}
      {[8, 15, 22].map((x) => (
        <line key={x} x1={x} y1="6" x2={x} y2={raised ? 13 : 28} stroke={color} strokeWidth="2.2" />
      ))}
      {!raised && [11, 18].map((x) => <line key={`c${x}`} x1="3" y1={x} x2="27" y2={x} stroke={color} strokeWidth="1.6" />)}
      {!raised && [8, 15, 22].map((x) => <path key={`s${x}`} d={`M${x - 3} 28 L${x} 32 L${x + 3} 28 Z`} fill={color} />)}
    </svg>
  );
}

function GateRow({ c }: { c: AuthorityCondition }) {
  const met = c.status === "met";
  const unmet = c.status === "unmet";
  const color = met ? "#e8703a" : unmet ? "#8a3b3b" : "#c9a23a";
  const tag = met ? "✓ met" : unmet ? "✕ unmet" : "? check";
  return (
    <li className={`flex items-center gap-3 rounded border bg-black/20 px-3 py-2 transition-colors ${met ? "border-ember-bright/40" : "border-steel-dark/70"}`}>
      <Portcullis raised={met} color={color} />
      <div className="min-w-0 flex-1">
        <p className="font-display text-[13px] leading-snug text-bone">{c.text}</p>
        {c.kind && <p className="font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute/70">{c.kind}</p>}
      </div>
      <span className="shrink-0 font-display text-sm font-light uppercase tracking-[0.14em]" style={{ color }}>{tag}</span>
    </li>
  );
}

function PowerTile({ a, lit }: { a: Authority; lit: boolean }) {
  return (
    <div className={`relative overflow-hidden rounded border px-3 py-3 transition-all ${lit ? "border-ember-bright/60 bg-ember/10 shadow-ember-soft" : "border-steel-dark/60 bg-black/20 opacity-60"}`}>
      <div className="flex items-start gap-2.5">
        <svg width="22" height="26" viewBox="0 0 22 26" className="mt-0.5 shrink-0" style={lit ? { filter: "drop-shadow(0 0 4px #e8703a)" } : undefined}>
          <path d="M11 1 L21 4 V12 Q21 21 11 25 Q1 21 1 12 V4 Z" fill={lit ? "#e8703a" : "#3a4654"} fillOpacity={lit ? 0.9 : 1} stroke={lit ? "#f0915f" : "#4a5664"} strokeWidth="1" />
        </svg>
        <div className="min-w-0">
          <p className={`font-display text-[13px] font-medium leading-tight ${lit ? "text-bone" : "text-bone-mute"}`}>
            {a.title.replace(/^Trustee power — /, "").replace(/^[^—]*— /, "")}
          </p>
          <p className="mt-1 font-display text-base leading-snug text-bone-mute">{a.scope}</p>
          {(a.scope_tags ?? []).length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {a.scope_tags.map((t) => (
                <span key={t} className="rounded bg-blue-bright/15 px-1.5 py-0.5 font-display text-sm font-light uppercase tracking-[0.1em] text-blue-bright">{t.replace(/_/g, " ")}</span>
              ))}
            </div>
          )}
        </div>
      </div>
      {lit && <span className="absolute right-2 top-2 inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow" />}
    </div>
  );
}

/* ===================== COUNTDOWN (date_range) ===================== */

function Countdown({ group, onClose }: { group: Authority[]; onClose: () => void }) {
  const a = group[0]!;
  const eff = a.effective_date ? new Date(a.effective_date).getTime() : null;
  const exp = a.expiration_date ? new Date(a.expiration_date).getTime() : null;
  const now = Date.now();
  const pct = eff && exp && exp > eff ? Math.min(Math.max((now - eff) / (exp - eff), 0), 1) : 0;
  const daysLeft = exp ? Math.round((exp - now) / 86400000) : null;
  const expired = exp ? now > exp : false;
  const statusColor = expired ? "#8a3b3b" : a.status === "in_force" ? "#e8703a" : "#6b7480";

  return (
    <Shell kind="time-bound instrument · the countdown" name={instrumentName(a.source_label, a.title)}
      grantor={a.grantor} grantee={a.grantee}
      statusLabel={expired ? "expired" : a.status.replace(/_/g, " ")} statusColor={statusColor} onClose={onClose}>

      {/* validity bar */}
      <div className="rounded border border-steel-dark/70 bg-black/30 px-4 py-4">
        <div className="mb-2 flex items-center justify-between font-display text-base font-light uppercase tracking-[0.1em] text-bone-mute">
          <span>{fmtDate(a.effective_date)}</span>
          <span className={expired ? "text-[#d39a9a]" : "text-ember-bright"}>
            {daysLeft === null ? "ongoing" : expired ? "expired" : `${daysLeft.toLocaleString()} days remaining`}
          </span>
          <span>{fmtDate(a.expiration_date)}</span>
        </div>
        <div className="relative h-3 w-full overflow-hidden rounded-full bg-steel-dark">
          <div className="absolute left-0 top-0 h-full rounded-full" style={{ width: `${pct * 100}%`, background: expired ? "#8a3b3b" : "#e8703a", boxShadow: expired ? "none" : "0 0 10px #e8703a" }} />
          {/* NOW marker */}
          {pct > 0 && pct < 1 && (
            <div className="absolute top-1/2 h-4 w-[2px] -translate-y-1/2 bg-bone" style={{ left: `calc(${pct * 100}% - 1px)`, boxShadow: "0 0 6px #e7e2d6" }} />
          )}
        </div>
        <p className="mt-1.5 text-center font-display text-sm font-light uppercase tracking-[0.2em] text-bone-mute/60">▲ now</p>
      </div>

      {/* authorized actions */}
      <div className="mt-4">
        <p className="mb-2 font-display text-base font-light uppercase tracking-[0.14em] text-bone-dim">it authorizes</p>
        <p className="font-display text-[13px] leading-relaxed text-bone-dim">{a.scope}</p>
        {(a.scope_tags ?? []).length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {a.scope_tags.map((t) => (
              <span key={t} className="rounded border border-ember-bright/40 bg-ember/10 px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.12em] text-ember-bright shadow-ember-soft">{t.replace(/_/g, " ")}</span>
            ))}
          </div>
        )}
      </div>

      {a.verbatim_quote && (
        <VerbatimClause a={a} />
      )}

      {((a.conditions ?? []).length > 0) && (
        <div className="mt-4">
          <p className="mb-2 font-display text-base font-light uppercase tracking-[0.14em] text-bone-dim">conditions</p>
          <div className="flex flex-wrap gap-2">
            {a.conditions.map((c, i) => (
              <span key={i} className={`rounded border px-2 py-1 font-display text-[12px] ${c.status === "met" ? "border-ember-bright/40 text-bone" : "border-steel-dark/70 text-bone-mute"}`}>
                {c.status === "met" ? "✓" : "•"} {c.text}
              </span>
            ))}
          </div>
        </div>
      )}

      {((a.termination_triggers ?? []).length > 0) && (
        <div className="mt-4">
          <p className="mb-2 font-display text-base font-light uppercase tracking-[0.14em] text-bone-dim">the wall breaches if</p>
          <div className="flex flex-wrap gap-2">
            {a.termination_triggers.map((t, i) => (
              <span key={i} className="rounded border border-[#8a3b3b]/60 bg-[#8a3b3b]/10 px-2.5 py-1 font-display text-[12px] text-[#d39a9a]">⚔ {t.text}</span>
            ))}
          </div>
        </div>
      )}
    </Shell>
  );
}


/** The instrument's exact operative language — verified verbatim against the
 *  source chunk by the build pipeline, with view-in-source. Attorneys get
 *  funny with words; this shows the words. */
function VerbatimClause({ a }: { a: import("../../hooks/useCaseAuthorities").Authority }) {
  const [open, setOpen] = useState(false);
  const ctx = useChunkContext(a.quote_chunk_id, open);
  return (
    <div className="mt-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <p className="font-display text-base font-light uppercase tracking-[0.14em] text-bone-dim">the clause — exact language</p>
        {a.quote_verified ? (
          <span className="rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.14em]" style={{ background: "#5a8a5a", color: "#0c0f12" }}>
            ✓ verified verbatim against source
          </span>
        ) : (
          <span className="rounded px-2 py-0.5 font-display text-sm font-light uppercase tracking-[0.14em]" style={{ background: "#c9a23a", color: "#0c0f12" }}>
            ≈ could not verify exactly — read the source
          </span>
        )}
      </div>
      <blockquote className="rounded border-l-4 border-ember-bright/60 bg-black/30 px-4 py-3 font-display text-[13px] italic leading-relaxed text-bone">
        &ldquo;{a.verbatim_quote}&rdquo;
      </blockquote>
      {a.quote_chunk_id && (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="mt-1.5 font-display text-sm font-light uppercase tracking-[0.1em] text-blue-bright/80 hover:text-blue-bright"
        >
          {open ? "▾ hide source" : "▸ view in source document"}
        </button>
      )}
      {open && (
        <div className="mt-2 rounded border border-steel-dark/50 bg-charcoal/60 p-2">
          {ctx.isLoading && <p className="font-display text-sm text-bone-mute">// opening the instrument…</p>}
          {ctx.error && <p className="font-display text-sm text-ember-bright">// {ctx.error.message}</p>}
          {ctx.data && (
            <div>
              <p className="mb-1.5 font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute/70">
                {ctx.data.source_label}{ctx.data.evidence_kind ? ` · ${ctx.data.evidence_kind.replace(/_/g, " ")}` : ""}
              </p>
              <div className="space-y-1">
                {ctx.data.chunks.map((line) => (
                  <pre key={line.chunk_index}
                       className={`overflow-x-auto whitespace-pre-wrap rounded px-2 py-1 font-display text-base leading-snug ${
                         line.is_center ? "bg-ember/10 text-bone shadow-ember-soft" : "text-bone-mute/70"
                       }`}>
                    {line.body}
                  </pre>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
